import type { ReactNode, RefObject } from 'react';
import { useRef } from 'react';

import { memoGeneric } from '@/js/libs/memo/index.ts';

import { Button } from '../../Controls/Button/index.tsx';
import { NothingFound } from '../../Elements/Images/index.ts';
import { Loader } from '../../Elements/Loader/index.tsx';
import { Tooltip } from '../../Feedback/Tooltip/index.tsx';
import { FOOTER_HEIGHT, ITEM_BORDER_SIZE } from './constants.ts';
import { useScrollbarWidth } from './hooks/useScrollbarWidth.ts';
import { ListItem } from './ListItem.tsx';
import { Header, ListWrapper, ScrollableContainer, Footer, FooterInfo, FooterSortOrder, NoDataWrapper } from './styled.ts';
import type { FormatOptionLabel, Option, Translations } from './types.ts';
import { SortOrder } from './types.ts';

type ListProps<T> = {
  dataTestId?: string;
  options: Option<T>[];
  onBulkAction: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  error?: boolean;
  sortOrder: SortOrder;
  onChangeSortOrder: () => void;
  containerHeight: number;
  getOptionKey?: (option: Option<T>) => string;
  translations: Translations;
  wrapperRef?: RefObject<HTMLDivElement>;
  bulkActionIcon?: ReactNode;
  rowActionIcon?: ReactNode;
  onRowAction?: (option: Option<T>) => void;
  formatOptionLabel?: FormatOptionLabel<T>;
};

export const List = memoGeneric(function List<T>({
  options,
  onRowAction,
  rowActionIcon,
  onBulkAction,
  isLoading,
  disabled,
  error,
  sortOrder,
  onChangeSortOrder,
  containerHeight,
  getOptionKey,
  translations,
  formatOptionLabel,
  wrapperRef,
  bulkActionIcon,
  dataTestId = '',
}: ListProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasItems = options.length > 0;
  const scrollbarWidth = useScrollbarWidth(containerRef, options);

  return (
    <ListWrapper $error={error} data-testid={dataTestId} ref={wrapperRef} data-test-error={error ?? false}>
      {translations.header || bulkActionIcon ? (
        <Header>
          <span>{translations.header ?? ''}</span>
          {bulkActionIcon && (
            <Tooltip tooltip={translations.bulkActionTooltip} placement='top' disabled={disabled}>
              <Button onClick={onBulkAction} disabled={disabled} ghost dataTestId={`${dataTestId}-bulk-action-button`}>
                {bulkActionIcon}
              </Button>
            </Tooltip>
          )}
        </Header>
      ) : null}

      <ScrollableContainer
        height={hasItems || isLoading ? containerHeight : containerHeight + FOOTER_HEIGHT + ITEM_BORDER_SIZE}
        ref={containerRef}
        data-testid={`${dataTestId}-container`}>
        {options.map((option) => (
          <ListItem<T>
            key={getOptionKey?.(option) ?? String(option.value)}
            option={option}
            onRowAction={onRowAction}
            rowActionIcon={rowActionIcon}
            disabled={disabled}
            dataTestId={`${dataTestId}-item`}
            formatOptionLabel={formatOptionLabel}
            scrollbarWidth={scrollbarWidth}
          />
        ))}
        {!isLoading && !hasItems && (
          <NoDataWrapper data-testid={`${dataTestId}-placeholder`}>
            <NothingFound />
            {translations.noEntries}
          </NoDataWrapper>
        )}
      </ScrollableContainer>

      {(hasItems || isLoading) && (
        <Footer data-testid={`${dataTestId}-footer`}>
          <FooterSortOrder onClick={onChangeSortOrder} role='button' data-testid={`${dataTestId}-sort-order`}>
            {sortOrder === SortOrder.ASC ? 'A-Z' : 'Z-A'}
          </FooterSortOrder>
          <FooterInfo>{translations.footer ?? ''}</FooterInfo>
        </Footer>
      )}

      {isLoading && <Loader cover dataTestId={`${dataTestId}-loader`} />}
    </ListWrapper>
  );
});
